<template>
  <div class="thank-you-page">
    <HeaderView />
    <main class="thank-you-content">
      <div class="container">
        <div class="success-card">
          <div class="success-icon">
            <svg
              width="80"
              height="80"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>

          <h1 class="success-title">¡Gracias por tu interés!</h1>
          <p class="success-message">
            Tu solicitud de demo ha sido registrada exitosamente. Nuestro equipo se pondrá en
            contacto contigo pronto para coordinar una demostración personalizada de FourinTrack.
          </p>

          <!--  <div class="next-steps">
            <h2>¿Qué sigue?</h2>
            <ul>
              <li>
                <span class="step-icon">📧</span>
                <div>
                  <strong>Revisa tu correo</strong>
                  <p>Te enviaremos un email de confirmación en los próximos minutos</p>
                </div>
              </li>
              <li>
                <span class="step-icon">📞</span>
                <div>
                  <strong>Te contactaremos</strong>
                  <p>Nuestro equipo se comunicará contigo en un plazo de 24 horas</p>
                </div>
              </li>
              <li>
                <span class="step-icon">🎯</span>
                <div>
                  <strong>Agendamos tu demo</strong>
                  <p>Coordinaremos una demostración adaptada a las necesidades de tu empresa</p>
                </div>
              </li>
            </ul>
          </div> -->

          <div class="actions">
            <router-link to="/" class="btn-primary">Volver al inicio</router-link>
            <router-link to="/precios" class="btn-secondary"
              >Conocer más sobre FourinTrack</router-link
            >
          </div>
        </div>
      </div>
    </main>
    <FooterView />
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { useRoute } from 'vue-router'
import HeaderView from '@/components/HeaderView.vue'
import FooterView from '@/components/FooterView.vue'
import { useSEO } from '@/composables/useSEO'

const route = useRoute()
const { updateMetaTags } = useSEO()

onMounted(() => {
  // SEO para la página de agradecimiento
  updateMetaTags({
    title: 'Gracias por tu solicitud - FourinTrack Demo | Fourin',
    description:
      'Tu solicitud de demo ha sido registrada. Nuestro equipo se pondrá en contacto contigo pronto.',
    url: 'https://fourin.co/gracias-demo',
    canonical: 'https://fourin.co/gracias-demo',
    type: 'website',
  })

  // Agregar meta tag robots para no indexar esta página
  let robotsMeta = document.querySelector('meta[name="robots"]')
  if (!robotsMeta) {
    robotsMeta = document.createElement('meta')
    robotsMeta.setAttribute('name', 'robots')
    document.head.appendChild(robotsMeta)
  }
  robotsMeta.setAttribute('content', 'noindex, nofollow')

  // Google Ads Conversion Tracking con GA4
  // Como tu fuente es Google Analytics (GA4), debemos disparar el evento de GA4 directamente
  const trackConversion = () => {
    if (typeof window === 'undefined') return

    // Esperar a que gtag esté disponible (puede tardar un momento en cargar)
    const checkGtag = () => {
      if ((window as any).gtag) {
        const transactionId = (route.query.id as string) || `demo_${Date.now()}`

        // Disparar el evento de GA4 configurado en Google Ads
        // Evento: ads_conversion_SUBMIT_LEAD_FORM_1
        // Este evento está conectado a tu conversión de Google Ads
        ;(window as any).gtag('event', 'ads_conversion_SUBMIT_LEAD_FORM_1', {
          value: 15000.0,
          currency: 'COP', // Usando COP según tu configuración
          transaction_id: transactionId,
        })

        // Evento adicional para Google Analytics (opcional, para tracking interno)
        ;(window as any).gtag('event', 'submit_form', {
          event_category: 'Demo Request',
          event_label: 'Products Demo Form',
          value: 1,
        })

        console.log('✅ Google Ads conversion event fired (GA4)', {
          event: 'ads_conversion_SUBMIT_LEAD_FORM_1',
          transactionId,
          currency: 'COP',
        })
      } else {
        // Reintentar después de 500ms si gtag aún no está disponible
        setTimeout(checkGtag, 500)
      }
    }

    // Iniciar verificación después de un pequeño delay para asegurar que gtag esté cargado
    setTimeout(checkGtag, 100)
  }

  trackConversion()
})
</script>

<style scoped>
/* ===== MOBILE FIRST - Estilos base para móvil ===== */

.thank-you-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.thank-you-content {
  flex: 1;
  padding: 80px 16px 60px;
}

.container {
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  padding: 0;
}

.success-card {
  background: white;
  border-radius: 16px;
  padding: 32px 0px;
  text-align: center;
}

.success-icon {
  width: 100px;
  height: 100px;
  margin: 0 auto 24px;
  background: linear-gradient(135deg, #129bef 0%, #4ab7ff 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  animation: scaleIn 0.5s ease-out;
}

@keyframes scaleIn {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.success-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
  line-height: 1.2;
}

.success-message {
  font-size: 1rem;
  line-height: 1.6;
  color: #64748b;
  margin-bottom: 32px;
  max-width: 100%;
}

.next-steps {
  text-align: left;
  margin: 32px 0;
  padding: 24px 20px;
  background: #f8fafc;
  border-radius: 12px;
}

.next-steps h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 20px;
  text-align: center;
}

.next-steps ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.next-steps li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.step-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
  line-height: 1;
}

.next-steps li div {
  flex: 1;
}

.next-steps li strong {
  display: block;
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

.next-steps li p {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}

.actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 32px;
}

.btn-primary,
.btn-secondary {
  padding: 14px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  text-align: center;
  box-shadow: none;
}

.btn-primary {
  background: linear-gradient(45deg, #129bef, #4ab7ff);
  color: white;
  border: none;
}

.btn-primary:active {
  transform: translateY(0);
}

.btn-secondary {
  background: white;
  color: #129bef;
  border: 2px solid #129bef;
}

.btn-secondary:active {
  background: #f8fafc;
  transform: translateY(0);
}

/* ===== TABLET - 481px y superior ===== */
@media (min-width: 481px) {
  .thank-you-content {
    padding: 100px 24px 80px;
  }

  .success-card {
    padding: 40px 32px;
    border-radius: 20px;
  }

  .success-icon {
    width: 100px;
    height: 100px;
    margin-bottom: 28px;
  }

  .success-title {
    font-size: 2rem;
    margin-bottom: 18px;
  }

  .success-message {
    font-size: 1.0625rem;
    margin-bottom: 36px;
  }

  .next-steps {
    padding: 32px 28px;
    margin: 40px 0;
  }

  .next-steps h2 {
    font-size: 1.375rem;
    margin-bottom: 24px;
  }

  .next-steps li {
    gap: 14px;
  }

  .step-icon {
    font-size: 1.75rem;
  }

  .next-steps li strong {
    font-size: 1.0625rem;
  }

  .next-steps li p {
    font-size: 0.9375rem;
  }

  .actions {
    flex-direction: row;
    gap: 16px;
    justify-content: center;
  }

  .btn-primary,
  .btn-secondary {
    width: auto;
    padding: 16px 32px;
    font-size: 16px;
  }

  .btn-primary:hover {
    transform: translateY(-2px);
  }

  .btn-secondary:hover {
    background: #f8fafc;
    transform: translateY(-2px);
  }
}

/* ===== DESKTOP - 769px y superior ===== */
@media (min-width: 769px) {
  .thank-you-content {
    padding: 120px 20px 80px;
  }

  .container {
    max-width: 800px;
  }

  .success-card {
    padding: 60px 40px;
    border-radius: 24px;
  }

  .success-icon {
    width: 120px;
    height: 120px;
    margin-bottom: 30px;
  }

  .success-title {
    font-size: 2.5rem;
    margin-bottom: 20px;
  }

  .success-message {
    font-size: 1.125rem;
    line-height: 1.7;
    margin-bottom: 40px;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }

  .next-steps {
    margin: 50px 0;
    padding: 40px;
    border-radius: 16px;
  }

  .next-steps h2 {
    font-size: 1.5rem;
    margin-bottom: 30px;
  }

  .next-steps ul {
    gap: 24px;
  }

  .next-steps li {
    gap: 16px;
  }

  .step-icon {
    font-size: 2rem;
  }

  .next-steps li strong {
    font-size: 1.125rem;
  }

  .next-steps li p {
    font-size: 0.9375rem;
    line-height: 1.6;
  }

  .actions {
    margin-top: 40px;
    flex-wrap: wrap;
  }
}

/* ===== LARGE DESKTOP - 1025px y superior ===== */
@media (min-width: 1025px) {
  .thank-you-content {
    padding: 140px 20px 100px;
  }
}
</style>
